import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Download, ArrowLeft, Copy, RefreshCw } from 'lucide-react';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const ResourceResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useAuthState(auth);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { showAlert } = useModal();

  // Get resource data from navigation state
  const { resourceContent, formData, selectedImage, fromLibrary } = location.state || {};

  // Redirect if no resource data
  if (!resourceContent) {
    navigate('/planner/resources');
    return null;
  }

  const handleCreateAnother = () => {
    // Navigate back to form with context preserved
    navigate('/planner/resources', { 
      state: { 
        previousResource: {
          resourceType: formData.resourceType,
          subject: formData.subject,
          yearGroup: formData.yearGroup
        }
      }
    });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      console.log('🔄 Starting PDF download...');
      
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          resourceResult: {
            content: resourceContent,
            formData: formData,
            // COMMENTED OUT: Image functionality temporarily disabled
            // Note: selectedImage removed - no longer sending image data
          },
          fileName: `${formData.resourceType}-${formData.topic.replace(/[^a-zA-Z0-9]/g, '-')}`,
          userLevel: formData.yearGroup,
        }),
      });
  
      console.log('📥 Response status:', response.status);
  
      if (response.ok) {
        console.log('✅ Response OK, processing PDF download...');
        
        const blob = await response.blob();
        console.log('📄 Blob type:', blob.type);
        console.log('📄 Blob size:', blob.size, 'bytes');
        
        // Validate PDF response
        if (blob.size < 100) {
          console.error('❌ Response too small, likely an error');
          await showAlert('Failed to generate PDF - response too small. Please try again.', 'Error');
          return;
        }
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `${formData.resourceType}-${formData.topic.replace(/[^a-zA-Z0-9]/g, '-')}-${timestamp}.pdf`;
        
        // Create download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 1000);
        
        console.log('✅ PDF download triggered successfully');
        // REMOVED: alert popup as requested
        
      } else {
        console.error('❌ Response not OK:', response.status);
        const errorText = await response.text();
        console.error('❌ Error details:', errorText);
        await showAlert(`Failed to generate PDF (${response.status}). Please try again.`, 'Error');
      }
    } catch (error) {
      console.error('❌ Download error:', error);
      await showAlert('Error during download. Please check your internet connection and try again.', 'Error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSaveToLibrary = async () => {
    if (!user || !resourceContent || fromLibrary) return;
    
    setIsSaving(true);
    try {
      const resourceData = {
        userId: user.uid,
        resourceContent: resourceContent,
        metadata: {
          resourceType: formData.resourceType,
          subject: formData.subject,
          yearGroup: formData.yearGroup,
          topic: formData.topic,
          requirements: formData.requirements,
          specialRequirements: formData.specialRequirements || '',
          // COMMENTED OUT: Image functionality temporarily disabled
          // selectedImage: selectedImage || null
        },
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp()
      };

      await addDoc(collection(db, 'userResources'), resourceData);
      setIsSaved(true);
      
      // Show success message
      await showAlert('Resource saved to your library! 📚', 'Saved');
    } catch (error) {
      console.error('Error saving resource:', error);
      await showAlert('Error saving resource. Please try again.', 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(resourceContent);
      await showAlert('Resource copied to clipboard!', 'Copied');
    } catch {
      await showAlert('Could not copy to clipboard. Please select and copy manually.', 'Error');
    }
  };

  const handleStartOver = () => {
    navigate('/planner');
  };

  const handleViewLibrary = () => {
    navigate('/planner/library');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="planner" />
      
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-coral-500 leading-tight mb-4">
              Your Resource is Ready!
            </h1>
            <p className="text-lg text-ink-700">
              {formData.resourceType} • {formData.subject} • {formData.yearGroup}
            </p>
            {fromLibrary && (
              <p className="text-sm text-ink-600 mt-2">
                📚 Viewing from your resource library
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
            {/* Save to Library Button - Only show if not from library and user is authenticated */}
            {!fromLibrary && user && (
              <button
                onClick={handleSaveToLibrary}
                disabled={isSaving || isSaved}
                className={`px-6 py-3 rounded-lg transition-all duration-300 font-semibold flex items-center ${
                  isSaved 
                    ? 'bg-green-500 text-white cursor-not-allowed'
                    : isSaving
                    ? 'bg-ink-400 text-white cursor-not-allowed'
                    : 'bg-coral-300 text-white hover:bg-coral-500'
                }`}
              >
                {isSaved ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Saved to Library
                  </>
                ) : isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                    </svg>
                    Save to Library
                  </>
                )}
              </button>
            )}

            {/* View Library Button - Show if from library */}
            {fromLibrary && (
              <button
                onClick={handleViewLibrary}
                className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
                Back to Library
              </button>
            )}

            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold flex items-center"
            >
              {isDownloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                  Creating PDF Document...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </>
              )}
            </button>

            <button
              onClick={handleCopyText}
              className="px-6 py-3 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold flex items-center"
            >
              <Copy className="w-5 h-5 mr-2" />
              Copy Text
            </button>
          </div>

          {/* Success Message for Saved Resource */}
          {isSaved && (
            <div className="mb-6 max-w-2xl mx-auto">
              <div className="bg-green-100 border-4 border-ink-900 text-green-700 px-4 py-3 rounded relative">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="font-medium">Resource saved successfully!</span>
                  <button
                    onClick={handleViewLibrary}
                    className="ml-4 underline hover:no-underline"
                  >
                    View in library →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Resource Content - IMPROVED FORMATTING */}
          <div className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal p-8 mb-8">
            <div className="max-w-none">
              {(() => {
                // Split content into lines and process for better formatting
                const lines = resourceContent.split('\n');
                let formattedContent = [];
                let currentSection = [];
                
                lines.forEach((line, index) => {
                  const trimmedLine = line.trim();
                  
                  // Check if this is likely a title (first significant line, centered content)
                  if (index < 5 && trimmedLine && !trimmedLine.startsWith('Name:') && !trimmedLine.startsWith('Date:') && 
                      !trimmedLine.startsWith('Class:') && !trimmedLine.startsWith('Instructions:')) {
                    formattedContent.push(
                      <h2 key={`title-${index}`} className="text-2xl font-bold text-center text-ink-800 mb-6">
                        {trimmedLine}
                      </h2>
                    );
                  }
                  // Student info fields (Name, Date, Class)
                  else if (trimmedLine.startsWith('Name:') || trimmedLine.startsWith('Date:') || trimmedLine.startsWith('Class:')) {
                    formattedContent.push(
                      <p key={`field-${index}`} className="text-left text-ink-700 mb-2 font-medium">
                        {trimmedLine}
                      </p>
                    );
                  }
                  // Instructions paragraph
                  else if (trimmedLine.startsWith('Instructions:')) {
                    formattedContent.push(
                      <div key={`instructions-${index}`} className="my-6 p-4 bg-coral-50 rounded-lg border-l-4 border-coral-400">
                        <p className="text-left text-ink-700 leading-relaxed">
                          {trimmedLine}
                        </p>
                      </div>
                    );
                  }
                  // Section dividers (---)
                  else if (trimmedLine === '---') {
                    formattedContent.push(
                      <hr key={`divider-${index}`} className="my-6 border-t-2 border-sand-300" />
                    );
                  }
                  // Section headers (words in asterisks)
                  else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                    const headerText = trimmedLine.replace(/\*\*/g, '');
                    formattedContent.push(
                      <h3 key={`header-${index}`} className="text-lg font-bold text-center text-ink-800 mt-6 mb-4">
                        {headerText}
                      </h3>
                    );
                  }
                  // Empty lines - preserve spacing
                  else if (!trimmedLine) {
                    if (currentSection.length > 0) {
                      formattedContent.push(
                        <div key={`section-${index}`} className="mb-4">
                          {currentSection.map((sectionLine, sIndex) => (
                            <p key={`line-${index}-${sIndex}`} className="text-left text-ink-700 leading-relaxed mb-2">
                              {sectionLine}
                            </p>
                          ))}
                        </div>
                      );
                      currentSection = [];
                    }
                  }
                  // Regular content lines
                  else {
                    currentSection.push(trimmedLine);
                  }
                });
                
                // Handle any remaining content
                if (currentSection.length > 0) {
                  formattedContent.push(
                    <div key="final-section" className="mb-4">
                      {currentSection.map((sectionLine, sIndex) => (
                        <p key={`final-line-${sIndex}`} className="text-left text-ink-700 leading-relaxed mb-2">
                          {sectionLine}
                        </p>
                      ))}
                    </div>
                  );
                }
                
                return formattedContent;
              })()}
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={handleCreateAnother}
              className="px-8 py-4 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold text-lg flex items-center"
            >
              <RefreshCw className="w-6 h-6 mr-3" />
              Create Another Resource
            </button>

            <button
              onClick={handleStartOver}
              className="px-8 py-4 bg-coral-300 text-white rounded-lg hover:bg-coral-500 transition-all duration-300 font-semibold text-lg"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Continue Creating Notice */}
          <div className="mt-8 text-center">
            <div className="bg-white bg-opacity-20 rounded-lg border-4 border-ink-900 p-4 max-w-2xl mx-auto">
              <p className="text-white text-sm">
                <span className="font-semibold">💡 Keep Creating:</span> Click "Create Another Resource" to maintain the same subject and year group for related materials. Your context will be preserved to make creating resource collections faster.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceResultPage;